/**
 * CohortOutcomesView — E3-S3 REVIEW-section view.
 *
 * Consumes GET /analytics/cohort-outcomes?orgPath=…&months=N from
 * chamber-v2. Renders a Command Data Officer's "Class at +12 months"
 * cut: cohort outcome distribution + per-KSB attrition correlation.
 *
 * ILLUSTRATIVE labeling is baked INTO THIS VIEW (per CEO directive):
 *   - Unmissable Alert banner at the top when `illustrative: true`.
 *   - Every row/label prefixed with "ILLUSTRATIVE" in the table.
 *   - The banner text comes from the API's `illustrative_reason` field.
 *
 * When live HRC feeds land, the API will flip `illustrative` to false;
 * this view then hides the banner automatically. No config toggle here.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import ScienceIcon from '@mui/icons-material/Science';
import { apiFetch } from '../providers/api-rest-provider';
import { canonicalOrgPath, isOrgPathWithin } from '../hooks/useRollup';

interface KsbCorrelation {
  tag: string;
  attainment: number;
  topQuartile: { n: number; attritionPct: number };
  bottomQuartile: { n: number; attritionPct: number };
}

interface CohortOutcomesResponse {
  illustrative: boolean;
  illustrative_reason: string;
  orgPath: string;
  cohortSize: number;
  personCount: number;
  windowMonths: number;
  outcomesByKind: Record<string, number>;
  outcomesByDisposition: Record<string, number>;
  personsWithAnyOutcome: number;
  personsWithNoOutcome: number;
  ksbCorrelation: KsbCorrelation[];
}

// Prefix every rendered field with this so a screenshot of the view
// carries the ILLUSTRATIVE label even without the banner in frame.
const IL = 'ILLUSTRATIVE · ';

const KIND_LABEL: Record<string, string> = {
  first_unit: 'First unit assignment',
  term_completion: 'First term completion',
  attrition: 'Attrition',
  officer_milestone: 'Officer milestone',
};

const MONTHS_CHOICES = [6, 12, 18, 24, 36];

export function CohortOutcomesView({ rootOrgPath }: { rootOrgPath: string }) {
  const [orgPath, setOrgPath] = useState(rootOrgPath);
  const [months, setMonths] = useState<number>(12);
  const queryOrgPath = canonicalOrgPath(orgPath) === orgPath &&
    isOrgPathWithin(rootOrgPath, orgPath) ? orgPath : null;
  const requestKey = queryOrgPath ? `${queryOrgPath}\u0000${months}` : '';
  const [result, setResult] = useState<{
    key: string;
    data: CohortOutcomesResponse | null;
    error: string | null;
    loading: boolean;
  }>({ key: '', data: null, error: null, loading: false });
  const current = result.key === requestKey
    ? result
    : { data: null, error: null, loading: Boolean(queryOrgPath) };

  useEffect(() => {
    setOrgPath(rootOrgPath);
  }, [rootOrgPath]);

  useEffect(() => {
    if (!queryOrgPath) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const path = `analytics/cohort-outcomes?orgPath=${encodeURIComponent(queryOrgPath)}&months=${months}`;
        const raw = await apiFetch(path, { signal: controller.signal }) as CohortOutcomesResponse;
        if (raw.orgPath !== queryOrgPath || raw.windowMonths !== months) {
          throw new Error('Analytics response dimensions did not match request');
        }
        if (!controller.signal.aborted) {
          setResult({ key: requestKey, data: raw, error: null, loading: false });
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setResult({
            key: requestKey,
            data: null,
            error: err instanceof Error ? err.message : String(err),
            loading: false,
          });
        }
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [queryOrgPath, months, requestKey]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <TimelineIcon color="primary" />
        <Typography variant="h5">Cohort Outcomes (Class at +N months)</Typography>
        <Chip label="E3-S3" size="small" color="warning" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Correlate a schoolhouse cohort&apos;s KSB attainment with the
        outcomes attached to those trainees N months post-graduation.
        Weakest KSB first; top vs bottom quartile attrition rates.
      </Typography>

      {/* ── ILLUSTRATIVE banner — baked into the view surface, not the docs. */}
      {current.data?.illustrative === true && (
        <Alert severity="warning" icon={<ScienceIcon />} sx={{ mb: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>ILLUSTRATIVE — do not brief as truth</AlertTitle>
          {current.data.illustrative_reason}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="orgPath"
              fullWidth
              value={orgPath}
              onChange={(e) => setOrgPath(e.target.value)}
              error={!queryOrgPath}
              helperText={queryOrgPath
                ? `Limited to ${rootOrgPath} and descendants`
                : `Enter ${rootOrgPath} or one of its descendants`}
            />
            <Select
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              size="small"
              inputProps={{ 'aria-label': 'window months' }}
              sx={{ minWidth: 140 }}
            >
              {MONTHS_CHOICES.map((m) => (
                <MenuItem key={m} value={m}>+{m} months</MenuItem>
              ))}
            </Select>
          </Stack>
        </CardContent>
      </Card>

      {current.loading && <LinearProgress sx={{ mb: 2 }} />}
      {current.error && (
        <Alert severity="error" sx={{ mb: 2 }}>{current.error}</Alert>
      )}

      {current.data && !current.loading && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`orgPath: ${current.data.orgPath}`} size="small" />
            <Chip label={`+${current.data.windowMonths} months`} size="small" color="primary" />
            <Chip label={`cohort: ${current.data.cohortSize}`} size="small" />
            <Chip label={`persons: ${current.data.personCount}`} size="small" />
            <Chip label={`with any outcome: ${current.data.personsWithAnyOutcome}`} size="small" />
            <Chip label={`no outcome: ${current.data.personsWithNoOutcome}`} size="small" variant="outlined" />
          </Stack>

          <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Outcomes by kind
            </Typography>
            {Object.keys(current.data.outcomesByKind).length === 0 ? (
              <Alert severity="info">
                No outcomes recorded in this window. Try widening the months window, or import outcomes via <code>POST /outcomes/import</code>.
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Kind</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(current.data.outcomesByKind).map(([kind, n]) => (
                    <TableRow key={kind}>
                      <TableCell>
                        <Typography variant="body2">
                          <b>{IL}</b>{KIND_LABEL[kind] ?? kind}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{n}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>

          {Object.keys(current.data.outcomesByDisposition).length > 0 && (
            <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Attrition by disposition
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Disposition</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(current.data.outcomesByDisposition).map(([d, n]) => (
                    <TableRow key={d}>
                      <TableCell>
                        <Typography variant="body2">
                          <b>{IL}</b>{d}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{n}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              KSB attainment × attrition rate
            </Typography>
            {current.data.ksbCorrelation.length === 0 ? (
              <Alert severity="info">
                Not enough attempts per KSB tag to compute quartile correlation (need ≥ 4 trainees per tag).
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>KSB tag</TableCell>
                    <TableCell align="right">Cohort attainment</TableCell>
                    <TableCell align="right">Top-quartile attrition</TableCell>
                    <TableCell align="right">Bottom-quartile attrition</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {current.data.ksbCorrelation.map((row) => (
                    <TableRow key={row.tag}>
                      <TableCell>
                        <Chip label={`${IL}${row.tag}`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{row.attainment}%</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={row.topQuartile.attritionPct >= 25 ? 'bold' : 'normal'}
                          color={row.topQuartile.attritionPct >= 25 ? 'error.main' : 'text.primary'}
                        >
                          {row.topQuartile.attritionPct}%
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            (n={row.topQuartile.n})
                          </Typography>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={row.bottomQuartile.attritionPct >= 25 ? 'bold' : 'normal'}
                          color={row.bottomQuartile.attritionPct >= 25 ? 'error.main' : 'text.primary'}
                        >
                          {row.bottomQuartile.attritionPct}%
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            (n={row.bottomQuartile.n})
                          </Typography>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}

      {!current.data && !current.loading && !current.error && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Enter an org path above to see the class-at-+N-months view.
        </Alert>
      )}
    </Box>
  );
}
